import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react'

import { Tooltip } from 'antd'
import { Locale as DateFnsLocale, isValid } from 'date-fns'
import { enUS, enAU, enGB, es, enCA } from 'date-fns/locale'
import { formatInTimeZone } from 'date-fns-tz'
import moment from 'moment-timezone'

import { Locale as BuildPassLocale, useLocale } from './localeContext'
import { useNavigation } from './navigationContext'

import config from '@/config/index'
import { useProject } from '@/context/projectContext'

import { QuerySupport } from 'types/navigation'

export type TimezonedDateProps = {
  date: (string | number | Date) | (string | number | Date)[]
  longForm?: boolean
  friendlyForm?: boolean
  includeTime?: boolean
  includeDate?: boolean
  includeYear?: boolean
  showTimezone?: boolean
  formatOverride?: string
  timezoneOverride?: string
  includeDay?: boolean
  locale?: BuildPassLocale
}

const SHORT_FRIENDLY_DATE_FORMAT = 'PP'
const SHORT_DATE_FORMAT = 'P'
const SHORT_TIME_FORMAT = 'p'
const LONG_DATE_FORMAT = 'PPPP'
const LONG_TIME_FORMAT = 'p'
const SHORT_FRIENDLY_TIME_FORMAT = `pppp`

export const DEFAULT_TIMEZONE = config.defaultTimezone
const USER_TIMEZONE = moment.tz.guess() || DEFAULT_TIMEZONE

const DATE_FNS_LOCALE_BY_BP_LOCALE: Record<BuildPassLocale, DateFnsLocale> = {
  [BuildPassLocale.enUS]: enUS,
  [BuildPassLocale.enAU]: enAU,
  [BuildPassLocale.enGB]: enGB,
  [BuildPassLocale.enCA]: enCA,
  [BuildPassLocale.esAU]: es,
  [BuildPassLocale.esGB]: es,
  [BuildPassLocale.esUS]: es,
  [BuildPassLocale.esCA]: es,
}

const getFormat = (
  includeDate: boolean,
  includeTime: boolean,
  longForm: boolean,
  friendlyForm: boolean,
  showTimezone: boolean,
  includeYear: boolean,
  includeDay: boolean
) => {
  const dateParts = [
    friendlyForm && includeDate ? SHORT_FRIENDLY_DATE_FORMAT : null,
    friendlyForm && includeTime ? SHORT_FRIENDLY_TIME_FORMAT : null,
    longForm && !friendlyForm && includeDate ? LONG_DATE_FORMAT : null,
    longForm && !friendlyForm && includeTime ? LONG_TIME_FORMAT : null,
    !longForm && !friendlyForm && includeDate ? SHORT_DATE_FORMAT : null,
    !longForm && !friendlyForm && includeTime ? SHORT_TIME_FORMAT : null,
  ]

  let format = dateParts.filter((x) => x).join(', ')
  if (includeYear) format += ' yyy'
  if (showTimezone) format += ' z'
  if (includeDay) format += ' (EEE)'
  return format
}

const formatDateStringBase = ({
  date,
  includeDate = true,
  includeTime = true,
  formatOverride,
  longForm = false,
  friendlyForm = false,
  timezoneOverride = USER_TIMEZONE,
  showTimezone,
  includeYear = false,
  includeDay = false,
  locale,
}: TimezonedDateProps) => {
  showTimezone =
    typeof showTimezone === 'undefined'
      ? USER_TIMEZONE !== timezoneOverride && includeTime && includeDate
      : showTimezone

  const formatString =
    formatOverride ||
    getFormat(
      includeDate,
      includeTime,
      longForm,
      friendlyForm,
      showTimezone,
      includeYear,
      includeDay
    )

  const dateFnsLocale = DATE_FNS_LOCALE_BY_BP_LOCALE[locale]

  if (Array.isArray(date)) {
    const validDates = date.filter((d) => isValid(new Date(d)))
    if (validDates.length === 0) return '-'

    return validDates
      .map((d) => {
        try {
          return formatInTimeZone(new Date(d), timezoneOverride, formatString, {
            locale: dateFnsLocale,
          })
        } catch {
          return 'Invalid date'
        }
      })
      .join(' - ')
  }

  if (!isValid(new Date(date))) {
    return '-'
  }

  try {
    return formatInTimeZone(new Date(date), timezoneOverride, formatString, {
      locale: dateFnsLocale,
    })
  } catch {
    return 'Invalid date'
  }
}

const formatDateRangeStringBase = (props: TimezonedDateProps) => {
  const { date, includeDate = true, includeTime = true } = props

  if (!date) {
    return '-'
  }

  return formatDateStringBase({ ...props, date, includeDate, includeTime })
}

type TimezoneContextValue = {
  formatDateString: (props: TimezonedDateProps) => string
  formatDateRangeString: (props: TimezonedDateProps) => string
  projectTimezone: string
  userTimezone: string
  userLocale: BuildPassLocale
  userInDifferentTimezone: boolean
}

export type FormatDateStringType = TimezoneContextValue['formatDateString']
export type FormateDateRangeStringType =
  TimezoneContextValue['formatDateRangeString']

const TimezoneContext = createContext<TimezoneContextValue>({
  projectTimezone: USER_TIMEZONE,
  userTimezone: USER_TIMEZONE,
  userLocale: (config.defaultLocale as BuildPassLocale) || BuildPassLocale.enAU,
  userInDifferentTimezone: false,
  formatDateString: (props: TimezonedDateProps) =>
    formatDateStringBase({
      timezoneOverride: USER_TIMEZONE,
      ...props,
    }),
  formatDateRangeString: (props: TimezonedDateProps) =>
    formatDateRangeStringBase({
      timezoneOverride: USER_TIMEZONE,
      ...props,
    }),
})

export const TimezoneProvider = ({
  timezoneOverride,
  children,
}: {
  timezoneOverride?: string
  children: React.ReactNode
}) => {
  const { currentNavItem } = useNavigation()
  const projectContext = useProject()
  const { locale } = useLocale()

  const useProjectTimezone = useMemo(
    () =>
      currentNavItem?.querySupport !== QuerySupport.BUILDER_ONLY &&
      !!projectContext?.project?.timezone,
    [currentNavItem, projectContext]
  )

  const projectTimezone = useMemo(
    () =>
      timezoneOverride ||
      (useProjectTimezone
        ? (projectContext?.project?.timezone ??
          projectContext?.project?.builder?.defaultTimezone ??
          USER_TIMEZONE)
        : USER_TIMEZONE),
    [
      projectContext?.project?.builder?.defaultTimezone,
      projectContext?.project?.timezone,
      timezoneOverride,
      useProjectTimezone,
    ]
  )

  useEffect(() => {
    if (projectTimezone && useProjectTimezone) {
      moment.tz.setDefault(projectTimezone)
    }
  }, [projectTimezone, useProjectTimezone])

  const userInDifferentTimezone = useMemo(
    () => USER_TIMEZONE !== projectTimezone,
    [projectTimezone]
  )

  const formatDateString = useCallback(
    (props: TimezonedDateProps) =>
      formatDateStringBase({
        timezoneOverride: projectTimezone,
        locale,
        ...props,
      }),
    [projectTimezone, locale]
  )

  const formatDateRangeString = useCallback(
    (props: TimezonedDateProps) =>
      formatDateRangeStringBase({
        timezoneOverride: projectTimezone,
        locale,
        ...props,
      }),
    [projectTimezone, locale]
  )

  return (
    <TimezoneContext.Provider
      value={{
        formatDateString,
        formatDateRangeString,
        projectTimezone,
        userTimezone: USER_TIMEZONE,
        userLocale:
          (config.defaultLocale as BuildPassLocale) || BuildPassLocale.enAU,
        userInDifferentTimezone,
      }}
    >
      {children}
    </TimezoneContext.Provider>
  )
}

export const TimezonedDate = (props: TimezonedDateProps) => {
  const {
    formatDateString,
    userInDifferentTimezone,
    userTimezone,
    userLocale,
  } = useTimezonedDates()
  if (!props.date) return '-'
  const zonedTime = formatDateString(props)

  let tooltip = undefined

  if (userInDifferentTimezone) {
    const userZonedTime = formatDateString({
      ...props,
      timezoneOverride: userTimezone,
      includeDate: true,
      locale: userLocale,
    })

    if (userZonedTime !== zonedTime) {
      tooltip = `Your timezone (${userTimezone}): ${userZonedTime}`
    }
  }

  return <Tooltip title={tooltip}>{zonedTime}</Tooltip>
}

export const useTimezonedDates = () => useContext(TimezoneContext)
